import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { studentService } from "../services/api/student";
import { FetchStudentsParams, Student, StudentFormData } from "../types/students";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { StudentFeeParticularsForm } from "../types/fees";

const ITEMS_PER_PAGE = 20;
export const STUDENT_KEYS = {
  all: ['student'] as const,
  schedules: (studentId: number) => [...STUDENT_KEYS.all, 'schedules', studentId] as const,
  results: (studentId: number) => [...STUDENT_KEYS.all, 'results', studentId] as const,
  result: (examId: number, studentId: number) => [...STUDENT_KEYS.all, 'result', examId, studentId] as const,
};

export const useStudents = (instituteId: number) => {
    const queryClient = useQueryClient();
    const [options, setOptions] = useState<Student[]>([]);
    const queryKeys = {
        students: (instituteId: number, filters?: FetchStudentsParams) => 
            filters ? ['students', instituteId, filters] : ['students', instituteId],
        studentById: (instituteId: number, id: number) => ['student', instituteId, id],
        studentSearch: (search: string) => ['students', 'search', search],
        studentFees: (instituteId: number, id: number) => ['studentFees', instituteId, id]
    };

    const useFetchTeacherStudents = (enabled = true) => useQuery({
        queryKey: ['students', 'teacher'],
        queryFn: () => studentService.getTeacherStudents(),
        enabled,
        staleTime: 60 * 1000,
    });

    const useFetchStudents = (filters: FetchStudentsParams, enabled = true) => useQuery({
        queryKey: queryKeys.students(instituteId, filters),
        queryFn: () => studentService.fetchStudents({
            ...filters,
            instituteId: instituteId,
        }),
        enabled: enabled && Boolean(instituteId && filters.limit && filters.page),
        staleTime: 30 * 1000,
    });

    const createStudent = useMutation({
        mutationFn: (data: StudentFormData) => studentService.create(data, instituteId),
        onSuccess: (_,variables) => {
            toast.success("Student Added Successfully");
            queryClient.invalidateQueries({ queryKey: queryKeys.students(instituteId) });
            queryClient.invalidateQueries({ queryKey: ['fees', instituteId, variables.classId] })
        },
        onError: (error) => {
            console.error("Error adding student:", error);
        }
    });
    const updateStudent = useMutation({
        mutationFn: ({ id, data }: { id: number; data: StudentFormData }) => studentService.update(id, data, instituteId),
        onSuccess: (_, variables) => {
            toast.success("Student updated Successfully");
            queryClient.invalidateQueries({ queryKey: queryKeys.students(instituteId) });
            queryClient.removeQueries({ queryKey: queryKeys.studentById(instituteId, variables.id) });
        },
        onError: (error) => {
            console.error("Error updating Student:", error);
        }
    });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const getStudent = (id: number) => useQuery({
        queryKey: queryKeys.studentById(instituteId, id),
        queryFn: () => studentService.getById(id),
        enabled: !!id,
        staleTime: 60 * 1000,
    });
    const useStudentSearch = (search: string, classId?: number, sectionId?: number) => {
        const query = useInfiniteQuery({
            queryKey: [...queryKeys.studentSearch(search), classId, sectionId],
            queryFn: ({ pageParam = 0 }) => studentService.fetchStudents({
                instituteId,
                name: search,
                classId,
                sectionId,
                page: pageParam,
                limit: ITEMS_PER_PAGE,
            }),
            getNextPageParam: (lastPage, allPages) =>
                lastPage.hasNextPage ? allPages.length : undefined,
            initialPageParam: 1
        });

        useEffect(() => {
            if (query.data) {
                const allStudents = query.data.pages.flatMap(page => page.data.flatMap(s => s));
                setOptions(allStudents);
            }
        }, [query.data]);

        return {
            ...query,
            options
        };
    };

    const useGetStudentFee = (id: number) => useQuery({
        queryKey: queryKeys.studentFees(instituteId, id),
        queryFn: () => studentService.getStudentFee(id),
        enabled: !!id
    });

    const useSaveStudentFee = useMutation({
        mutationFn: ({ data }: { data: StudentFeeParticularsForm }) => studentService.saveStudentFeeDetails(data, instituteId),
        onSuccess: (_, variables) => {
            toast.success("updated Fee Details Successfully");
            queryClient.invalidateQueries({ queryKey: queryKeys.studentFees(instituteId, variables.data.studentId) });
        },
        onError: (error) => {
            console.error("Error updating Fee Details:", error);
        }
    });

    const useStudentResults = (studentId: number) => {
        return useQuery({
            queryKey: STUDENT_KEYS.results(studentId),
            queryFn: () => studentService.getResults(studentId),
            staleTime: 2 * 60 * 1000,
        });
    };

    const useExamResult = (examId: number, studentId: number) => {
        return useQuery({
            queryKey: STUDENT_KEYS.result(examId, studentId),
            queryFn: () => studentService.getExamResult(examId, studentId),
            enabled: !!examId && !!studentId,
            staleTime: 2 * 60 * 1000,
        });
    };

    const useStudentSchedules = (studentId: number) => {
        return useQuery({
            queryKey: STUDENT_KEYS.schedules(studentId),
            queryFn: () => studentService.getSchedules(studentId),
            staleTime: 2 * 60 * 1000,
        });
    };

    const useGenerateBiodataLetter = (id: number, options?: { enabled?: boolean }) => useQuery({
        queryKey: ['biodataLetter', id],
        queryFn: () => studentService.generateBiodataLetter(id),
        enabled: !!id && (options?.enabled !== false)
    });

    return {
        useFetchTeacherStudents,
        useFetchStudents,
        createStudent,
        getStudent,
        updateStudent,
        useStudentSearch,
        useGetStudentFee,
        useSaveStudentFee,
        useStudentResults,
        useExamResult,
        useStudentSchedules,
        useGenerateBiodataLetter
    }
}